import {
  Box,
  Button,
  Flex,
  Heading,
  Table as ChakraTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Text,
  Badge,
  HStack,
  IconButton,
  useToast,
  Input,
  InputGroup,
  Select,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  FormControl,
  FormLabel,
  VStack,
} from '@chakra-ui/react'
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdminClawMachineCoin, AdminSeason } from '@olagg/db-types';
import useClawMachineCoins from './hooks/useClawMachineCoins';
import useSeasons from '../hooks/useSeasons';
import Pagination from '@olagg/ui-kit/Pagination';
import { AsyncSelect } from 'chakra-react-select';
import { AdminUserEndpoints, fetcher } from '@olagg/api-hooks';

type CreateMode = 'single' | 'bulk';

const CreateCoinsModal = ({
  isOpen,
  onClose,
  seasons,
  loadingSeasons,
  onSuccess
}: {
  isOpen: boolean;
  onClose: () => void;
  seasons: AdminSeason[];
  loadingSeasons: boolean;
  onSuccess: () => void;
}) => {
  const toast = useToast();
  const { createClawCoins, bulkCreateClawCoins, loading } = useClawMachineCoins();
  const [mode, setMode] = useState<CreateMode>('single');
  const [formData, setFormData] = useState({
    user_id: '',
    quantity: '',
    expiration_days: '',
    notes: '',
    notes_en: '',
    notes_pt: '',
  });
  const [selectedUser, setSelectedUser] = useState<{ value: string; label: string } | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [bulkResult, setBulkResult] = useState<{ message: string; csv: string; count: number } | null>(null);

  const loadUserOptions = useCallback(async (inputValue: string) => {
    try {
      if (!inputValue) {
        return [];
      }

      const endpoint = AdminUserEndpoints.all({
        page: 1,
        limit: 20,
        query: inputValue,
        country: undefined,
        withWallets: false
      });

      const response = await fetcher(endpoint);

      return response.users.map((user: any) => ({
        value: user.id.toString(),
        label: `${user.name} (${user.email}) - ID: ${user.id}`
      }));
    } catch (error) {
      console.error('Error loading users:', error);
      return [];
    }
  }, []);

  const resetForm = () => {
    setFormData({
      user_id: '',
      quantity: '',
      expiration_days: '',
      notes: '',
      notes_en: '',
      notes_pt: '',
    });
    setSelectedUser(null);
    setFile(null);
    setBulkResult(null);
  };

  const handleSubmit = async () => {
    if (mode === 'bulk') {
      if (!file) {
        toast({
          title: 'Error',
          description: 'Debes seleccionar un archivo',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      try {
        const formDataToSend = new FormData();
        formDataToSend.append('file', file);
        if (formData.quantity) formDataToSend.append('quantity', formData.quantity);
        if (formData.expiration_days) formDataToSend.append('expiration_days', formData.expiration_days);
        if (formData.notes) formDataToSend.append('notes', formData.notes);
        if (formData.notes_en) formDataToSend.append('extra_data[en][notes]', formData.notes_en);
        if (formData.notes_pt) formDataToSend.append('extra_data[pt][notes]', formData.notes_pt);

        const response = await bulkCreateClawCoins(formDataToSend);

        onSuccess();

        if (response.meta?.missingIdsCsv) {
          setBulkResult({
            message: `Se crearon ${response.meta.count} moneda(s), pero no se encontraron ${response.meta.missingIdsCount} usuario(s).`,
            csv: response.meta.missingIdsCsv,
            count: response.meta.missingIdsCount,
          });
          toast({
            title: 'Creación parcial',
            description: `Se crearon ${response.meta.count} moneda(s), pero ${response.meta.missingIdsCount} usuario(s) no fueron encontrados.`,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Éxito',
            description: response.meta?.message || 'Monedas creadas correctamente',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          resetForm();
          onClose();
        }
      } catch (error: any) {
        if (error.csv) {
          setBulkResult({
            message: `Error: ${error.errors}`,
            csv: error.csv,
            count: 0,
          });
        }
        toast({
          title: 'Error',
          description: `Error al crear las monedas: ${error.errors}`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      return;
    }

    if (!formData.user_id || !formData.quantity) {
      toast({
        title: 'Error',
        description: 'Todos los campos son requeridos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await createClawCoins({
        user_id: Number(formData.user_id),
        quantity: Number(formData.quantity),
        expiration_days: formData.expiration_days ? Number(formData.expiration_days) : undefined,
        notes: formData.notes || undefined,
        extra_data: {
          en: {
            notes: formData.notes_en || undefined,
          },
          pt: {
            notes: formData.notes_pt || undefined,
          },
        },
      });

      toast({
        title: 'Éxito',
        description: 'Monedas creadas correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      resetForm();
      onSuccess();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error al crear las monedas',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader>Crear Monedas</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Modo de creación</FormLabel>
              <HStack>
                <Button
                  size="sm"
                  colorScheme={mode === 'single' ? 'blue' : 'gray'}
                  variant={mode === 'single' ? 'solid' : 'outline'}
                  onClick={() => { setMode('single'); setFile(null); }}
                >
                  Usuario individual
                </Button>
                <Button
                  size="sm"
                  colorScheme={mode === 'bulk' ? 'blue' : 'gray'}
                  variant={mode === 'bulk' ? 'solid' : 'outline'}
                  onClick={() => { setMode('bulk'); setSelectedUser(null); setFormData({ ...formData, user_id: '' }); }}
                >
                  Carga masiva (CSV/XLSX)
                </Button>
              </HStack>
            </FormControl>

            {mode === 'single' ? (
              <FormControl isRequired>
                <FormLabel>Usuario</FormLabel>
                <AsyncSelect
                  placeholder="Buscar por nombre, email o ID..."
                  loadOptions={loadUserOptions}
                  value={selectedUser}
                  onChange={(option) => {
                    setSelectedUser(option);
                    setFormData({ ...formData, user_id: option?.value || '' });
                  }}
                  isClearable
                  noOptionsMessage={({ inputValue }) =>
                    !inputValue ? 'Escribe para buscar usuarios' : 'No se encontraron usuarios'
                  }
                  loadingMessage={() => 'Buscando usuarios...'}
                  chakraStyles={{
                    control: (provided) => ({
                      ...provided,
                      bg: 'gray.700',
                      borderColor: 'gray.600',
                    }),
                    menu: (provided) => ({
                      ...provided,
                      bg: 'gray.700',
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      bg: state.isFocused ? 'gray.600' : 'gray.700',
                      color: 'white',
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      color: 'white',
                    }),
                    input: (provided) => ({
                      ...provided,
                      color: 'white',
                    }),
                  }}
                />
              </FormControl>
            ) : (
              <FormControl isRequired>
                <FormLabel>Archivo (CSV o XLSX)</FormLabel>
                <Text fontSize="xs" color="gray.400" mb={2}>
                  El archivo debe contener una columna user_id con los IDs de los usuarios.
                </Text>
                <Input
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  bg="gray.700"
                  borderColor="gray.600"
                  p={1}
                  sx={{
                    '::file-selector-button': {
                      bg: 'gray.600',
                      color: 'white',
                      border: 'none',
                      borderRadius: 'md',
                      px: 3,
                      py: 1,
                      mr: 3,
                      cursor: 'pointer',
                    }
                  }}
                />
                {file && (
                  <Text fontSize="sm" color="green.300" mt={1}>
                    Archivo seleccionado: {file.name}
                  </Text>
                )}
              </FormControl>
            )}

            <FormControl isRequired={mode === 'single'}>
              <FormLabel>Cantidad</FormLabel>
              <Input
                type="number"
                placeholder="Ingrese la cantidad"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                bg="gray.700"
                borderColor="gray.600"
                min="1"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Expira en</FormLabel>
              <FormLabel fontSize={"sm"} color={"grey"}>Dejar vacío para que no expire</FormLabel>
              <Input
                type="number"
                placeholder="Ingrese los dias hasta la expiración"
                value={formData.expiration_days}
                onChange={(e) => setFormData({ ...formData, expiration_days: e.target.value })}
                bg="gray.700"
                borderColor="gray.600"
                min="1"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Notas en Español (Opcional)</FormLabel>
              <Input
                as="textarea"
                placeholder="Razón para dar las monedas..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                bg="gray.700"
                borderColor="gray.600"
                minH="100px"
                resize="vertical"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Notas en Inglés (Opcional)</FormLabel>
              <Input
                as="textarea"
                placeholder="Reason for giving coins..."
                value={formData.notes_en}
                onChange={(e) => setFormData({ ...formData, notes_en: e.target.value })}
                bg="gray.700"
                borderColor="gray.600"
                minH="100px"
                resize="vertical"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Notas en Portugués (Opcional)</FormLabel>
              <Input
                as="textarea"
                placeholder="Razão para dar moedas..."
                value={formData.notes_pt}
                onChange={(e) => setFormData({ ...formData, notes_pt: e.target.value })}
                bg="gray.700"
                borderColor="gray.600"
                minH="100px"
                resize="vertical"
              />
            </FormControl>
            {bulkResult && (
              <Box bg="yellow.800" p={4} borderRadius="md" border="1px" borderColor="yellow.600">
                <Text fontWeight="bold" mb={2}>{bulkResult.message}</Text>
                <Button
                  size="sm"
                  colorScheme="yellow"
                  onClick={() => {
                    const blob = new Blob([atob(bulkResult.csv)], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'missing_user_ids.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Descargar CSV con user IDs no encontrados
                </Button>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          {bulkResult ? (
            <Button colorScheme="blue" onClick={() => { resetForm(); onClose(); }}>
              Cerrar
            </Button>
          ) : (
            <>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={loading}
              >
                {mode === 'bulk' ? 'Crear masivamente' : 'Crear'}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ClawCoinsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { getClawCoins, loading } = useClawMachineCoins();
  const { getSeasons, loading: loadingSeasons } = useSeasons();
  const toast = useToast();
  const { isOpen: isCreateModalOpen, onOpen: onCreateModalOpen, onClose: onCreateModalClose } = useDisclosure();
  const [coins, setCoins] = useState<AdminClawMachineCoin[]>([]);
  const [seasons, setSeasons] = useState<AdminSeason[]>([]);
  const [count, setCount] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filter, setFilter] = useState({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 20),
  });
  const { page, limit, search, status } = filter;

  const onFilter = ({
    name,
    value
  }: { name: string, value: any }) => {
    const parsedValue = (name === 'page' || name === 'limit') ? Number(value) : value;
    setFilter({ ...filter, [name]: parsedValue });

    if (value) searchParams.set(name, value);
    else searchParams.delete(name);
    setSearchParams(searchParams);
  };

  const onClearFilters = () => {
    setFilter({
      search: '',
      status: '',
      page: 1,
      limit: 20,
    });
    setSearchParams(new URLSearchParams());
  };

  const fetchCoins = async () => {
    try {
      const response = await getClawCoins({ 
        page, 
        limit,
        search: debouncedSearch || undefined,
        status: status || undefined,
      });
      
      setCoins(response.clawMachineCoins);
      setCount(response.meta.totalCount);
    } catch (error) {
      console.error('Error fetching coins:', error);
    }
  };

  const fetchSeasons = async () => {
    try {
      const response = await getSeasons({ page: 1, limit: 100 });
      setSeasons(response.gameSeasons);
    } catch (error) {
      console.error('Error fetching seasons:', error);
    }
  };

  // Debounce search filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)

    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    fetchCoins();
  }, [page, limit, debouncedSearch, status]);

  useEffect(() => {
    fetchSeasons();
  }, []);

  useEffect(() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlSearch = searchParams.get('search');
    const urlGameSeasonId = searchParams.get('game_season_id');
    const urlStatus = searchParams.get('status');

    if (urlPage || urlLimit || urlSearch || urlGameSeasonId || urlStatus) {
      setFilter({
        search: urlSearch || '',
        status: urlStatus || '',
        page: Number(urlPage || 1),
        limit: Number(urlLimit || 20),
      });
    }
  }, []);

  const formatDate = (date: Date | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Center w='full' pb={4}>
      <Box color={'white'} w='full'>
        <Heading textAlign="center" mb={6}>
          Claw Machine Coins
        </Heading>

        {/* Botón de Crear */}
        <Flex w='full' justifyContent='flex-end' mb={4}>
          <Button 
            colorScheme="blue" 
            onClick={onCreateModalOpen}
          >
            Crear Monedas
          </Button>
        </Flex>

        {/* Filtros */}
        <Box
          mb={6}
          p={4}
          bg="gray.800"
          borderRadius="md"
          border="1px"
          borderColor="gray.600"
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
          gap={4}
        >
          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Buscar por nombre, email o ID
            </Text>
            <Input
              placeholder="Buscar..."
              value={search}
              onChange={(e) => onFilter({ name: 'search', value: e.target.value })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            />
          </Box>

          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Status
            </Text>
            <Select
              placeholder="Todos"
              value={status}
              onChange={(e) => onFilter({ name: 'status', value: e.target.value })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            >
              <option value="used">Usadas</option>
              <option value="available">Disponibles</option>
              <option value="expired">Expiradas</option>
            </Select>
          </Box>

          <Box gridColumn={{ base: '1', md: 'span 3' }}>
            <Button
              colorScheme="gray"
              variant="outline"
              onClick={onClearFilters}
              size="sm"
              w="full"
            >
              Limpiar filtros
            </Button>
          </Box>
        </Box>

        <Flex w='full' alignItems='center' gap='5' mb={4}>
          <Text color="gray.400">
            Total de monedas: <Text as="span" color="white" fontWeight="bold">{count}</Text>
          </Text>
        </Flex>

        {loading ? (
          <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
            <Spinner />
          </Box>
        ) : (
          coins?.length > 0 ? (
            <Flex w={'full'} pb={4} justifyContent='center' flexDirection={'column'}>
              <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
                <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="60vh">
                  <ChakraTable variant="simple">
                    <Thead position="sticky" top={0} bg="gray.800" zIndex={1}>
                      <Tr>
                        <Th color="white">ID</Th>
                        <Th color="white">Usuario</Th>
                        <Th color="white">Email</Th>
                        <Th color="white">Status</Th>
                        <Th color="white">Notas</Th>
                        <Th color="white">Usado el</Th>
                        <Th color="white">Creado el</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {coins.map((coin) => (
                        <Tr key={coin.id}>
                          <Td>{coin.id}</Td>
                          <Td>{coin.userInfo.name}</Td>
                          <Td fontSize="sm">{coin.userInfo.email}</Td>
                          <Td>
                            <Badge colorScheme={coin.usedAt ? "red" : coin.expired ? "gray" : "green"}>
                              {coin.usedAt ? "Usada" : coin.expired ? "Expirada" : "Disponible"}
                            </Badge>
                          </Td>
                          <Td fontSize="sm">{coin.notes}</Td>
                          <Td fontSize="sm">{formatDate(coin.usedAt)}</Td>
                          <Td fontSize="sm">{formatDate(coin.createdAt)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </ChakraTable>
                </TableContainer>
              </Flex>
            </Flex>
          ) : (
            <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
              <Text>No hay monedas registradas</Text>
            </Box>
          )
        )}

        <Pagination
          params={{ limit, page, count }}
          onClick={page =>
            onFilter({
              name: 'page',
              value: String(page + 1)
            })
          }
          onLimitChange={val =>
            onFilter({
              name: 'limit',
              value: val
            })
          }
          hideLimitInput
        />

        {/* Modal de Creación */}
        <CreateCoinsModal 
          isOpen={isCreateModalOpen}
          onClose={onCreateModalClose}
          seasons={seasons}
          loadingSeasons={loadingSeasons}
          onSuccess={fetchCoins}
        />
      </Box>
    </Center>
  )
}

export default ClawCoinsList;